import type { SimulatorType } from '@/ui'
import type { FontData } from '@jobilla/nexus-sdk'
import { atom } from 'jotai'
import { focusAtom } from 'jotai-optics'
import { atomFamily } from 'jotai/utils'
import type { OrgAssets } from '../../views'
import type { ExternalCanvasData } from './container'
import { CanvasData } from './data'
import type { TextRefTarget } from './data/v1'

export type SafeZone = { top: number; right: number; bottom: number; left: number }

export type CanvasSize = { width: number; height: number }

export const canvasSizeAtom = atom<CanvasSize>({ width: 0, height: 0 })

// Root atom — set once on store init, updated by actions
export const documentAtom = atom<CanvasData>({
	version: 1,
	layers: {},
	layerTree: [],
})

// Explicitly called once in the container
export const onCommitAtom = atom<((doc: CanvasData) => void) | null>(null)

export const layersAtom = focusAtom(documentAtom, (o) => o.prop('layers'))

export const layerAtomFamily = atomFamily((id: string) =>
	focusAtom(documentAtom, (o) =>
		o.prop('layers')
			.prop(id))
)

export const layerTreeAtom = focusAtom(documentAtom, (o) => o.prop('layerTree'))

export const selectedLayerIdAtom = atom<string | null>(null)

export const highlightedLayerIdAtom = atom<string | null>(null)

// --- External canvas data (title, subtitle, cta, location, etc.) ---

export const externalCanvasDataAtom = atom<ExternalCanvasData>({
	title: '',
	subtitle: '',
	location: '',
	cta: '',
	logoUrl: '',
	backgroundUrl: '',
	foregroundColor: '',
	backgroundColor: '',
	accentForegroundColor: '',
	accentBackgroundColor: '',
})

export const onExternalCanvasDataChangeAtom = atom<((changes: Partial<ExternalCanvasData>) => void) | null>(null)

export const externalTitleAtom = focusAtom(externalCanvasDataAtom, (o) => o.prop('title'))
export const externalSubtitleAtom = focusAtom(externalCanvasDataAtom, (o) => o.prop('subtitle'))
export const externalCtaAtom = focusAtom(externalCanvasDataAtom, (o) => o.prop('cta'))
export const externalLocationAtom = focusAtom(externalCanvasDataAtom, (o) => o.prop('location'))

export const externalRefAtoms: Record<TextRefTarget, typeof externalTitleAtom> = {
	title: externalTitleAtom,
	subtitle: externalSubtitleAtom,
	cta: externalCtaAtom,
	location: externalLocationAtom,
}

export const externalLogoUrlAtom = focusAtom(externalCanvasDataAtom, (o) => o.prop('logoUrl'))

export const externalImageRefAtoms = {
	logo: externalLogoUrlAtom,
}

// --- Org assets (threaded from route → container → inspectors) ---

// Tracks which layer IDs are expanded in the layer list UI
export const expandedLayerIdsAtom = atom(new Set<string>())

export const canvasOrgAssetsAtom = atom<OrgAssets>({ media: [] })
export const canvasOnAssetsUploadAtom = atom<((files: FileList, collection?: string) => void) | null>(null)
export const canvasCustomFontsAtom = atom<FontData[]>([])
export const canvasCustomFontsLoadingAtom = atom<boolean>(false)

/**
 * Tracks how many layers are currently waiting for a font file to load.
 * When > 0, the canvas should show a loading indicator and block export.
 */
export const fontLoadingCountAtom = atom(0)

/** The canvas root DOM element — used to scope querySelector to the correct canvas instance. */
export const canvasRootElAtom = atom<HTMLElement | null>(null)

/** Whether the external text fields are currently being generated by AI. */
export const canvasIsGeneratingAtom = atom(false)

export const canvasSimulatorTypeAtom = atom<SimulatorType | null>(null)
